import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useUser } from './hooks/useUser';
import { AppLoader } from './components';
import PurchaseSummary from './views/SummaryPage/PurchaseSummary';
import BrrrrSummary from './views/SummaryPage/BrrrrSummary';
import RentRoll from './views/SummaryPage/RentRoll';
import RentRollPage from './views/RentRoll';
import ComparisonProjections from './views/Projections';
import ComparisonCashFlow from './views/CashFlow';
import PurchaseWorksheet from './views/PurchaseWorksheet';
import RefinancingWorksheet from './views/RefinanceWorksheet';
import RehabWorksheet from './views/RehabWorksheet';
import PropertyPhotoPage from './views/PropertyPhotoPage';
import BuyAndHoldSummary from './views/SummaryPage/BuyAndHoldSummary';
import PropertyMapPage from './views/PropertyMap';
import FlipSummary from './views/FlipSummary';
import FlipWorksheet from './views/FlipWorksheet';
import SummaryPage from './views/SummaryPage';
import PropertyOverview from './views/PropertyOverview';

import PurchaseSummaryV2 from './views/PurchaseSummaryViewV2';
import RehabSummaryV2 from './views/RehabSummaryViewV2';
import BRRRRSummaryV2 from './views/BRRRRSummaryViewV2';

const Layout = lazy(() => import('./components/Layout'));

const DashboardPage = lazy(() =>
  import(/* webpackChunkName: "dashboard" */ './views/DashboardPage')
);

const LandingPage = lazy(() =>
  import(/* webpackChunkName: "landing" */ './views/LandingPage')
);
const PropertiesPage = lazy(() =>
  import(/* webpackChunkName: "properties" */ './views/PropertiesPage')
);
const PropertyLandingPage = lazy(() =>
  import(/* webpackChunkName: "report" */ './views/PropertyLandingPage')
);
const UserProfilePage = lazy(() =>
  import(/* webpackChunkName: "user" */ './views/UserProfilePage')
);
const CapRateCalculatorPage = lazy(() =>
  import(
    /* webpackChunkName: "cap-rate-calculator" */ './views/CapRateCalculatorPage'
  )
);
const OfferCalculatorPage = lazy(() =>
  import(
    /* webpackChunkName: "offer-calculator" */ './views/OfferCalculatorPage'
  )
);
const PropertyEvaluatorPage = lazy(() =>
  import(
    /* webpackChunkName: "property-evaluator-page" */ './views/PropertyEvaluatorPage'
  )
);
const CapRateCalculatorEmbed = lazy(() =>
  import(
    /* webpackChunkName: "cap-rate-calculator-embed" */ './components/calculators/CapRateCalculator'
  )
);

const OfferCalculatorEmbed = lazy(() =>
  import(
    /* webpackChunkName: "offer-calculator-embed" */ './components/calculators/OfferCalculator'
  )
);

const PropertyEvaluatorEmbed = lazy(() =>
  import(
    /* webpackChunkName: "property-evaluator-embed" */ './components/calculators/PropertyEvaluator'
  )
);

const PurchaseWorksheetEmbed = lazy(() =>
  import(
    /* webpackChunkName: "purchase-worksheet-embed" */ './views/PurchaseWorksheetEmbed'
  )
);

const SettingsPage = lazy(() =>
  import(/* webpackChunkName: "settings" */ './views/Settings')
);

const NotFound = lazy(() =>
  import(/* webpackChunkName: "404" */ './views/NotFound')
);

import LoginPage from './views/auth/LogIn';
import SignUpPage from './views/auth/SignUp';

function AppRoutes() {
  let { user } = useUser();

  if (user?.loading) return <AppLoader />;

  return (
    <Suspense fallback={<AppLoader />}>
      {/* <Layout> */}
      <Routes>
        <Route path="login" element={<LoginPage />} />
        <Route path="sign-up" element={<SignUpPage />} />
        <Route element={<Layout />}>
          <Route
            path="cap-rate-calculator"
            element={<CapRateCalculatorPage />}
          />
          {/* shared routes */}
          <Route
            path="cap-rate-calculator"
            element={<CapRateCalculatorPage />}
          />
          <Route path="offer-calculator" element={<OfferCalculatorPage />} />
          <Route
            path="property-evaluator"
            element={<PropertyEvaluatorPage />}
          />
          <Route
            path="embedded/cap-rate-calculator"
            element={<CapRateCalculatorEmbed />}
          />
          <Route
            path="embedded/offer-calculator"
            element={<OfferCalculatorEmbed />}
          />
          <Route
            path="embedded/property-evaluator"
            element={<PropertyEvaluatorEmbed />}
          />
          <Route
            path="embedded/purchase-worksheet"
            element={<PurchaseWorksheetEmbed />}
          />
          <Route
            path="sharing/:uid/:propertyId"
            element={<PropertyLandingPage />}
          >
            <Route index element={<Navigate to="summary" />} />
            <Route path="flip-analysis" element={<FlipSummary />} />
            <Route path="photos" element={<PropertyPhotoPage />} />
            <Route path="overview" element={<PropertyOverview />} />
            <Route path="map" element={<PropertyMapPage />} />
            <Route path="summary" element={<SummaryPage />}>
              <Route index element={<Navigate to="purchase-analysis" />} />
              <Route path="projections" element={<ComparisonProjections />} />

              <Route path="cash-flow" element={<ComparisonCashFlow />} />
              <Route path="purchase-analysis" element={<PurchaseSummaryV2 />} />
              <Route path="buy-and-hold" element={<RehabSummaryV2 />} />
              <Route path="rent-roll" element={<RentRoll />} />
              <Route path="brrrr-analysis" element={<BRRRRSummaryV2 />} />
              <Route path="photos" element={<PropertyPhotoPage />} />
              <Route path="map" element={<PropertyMapPage />} />
            </Route>
          </Route>
          <Route path="not-found" element={<NotFound />} />

          {/* different target for users and non users */}
          <Route
            path="/"
            element={user ? <Navigate to="/dashboard" /> : <LandingPage />}
          />
          <Route
            path="properties"
            element={user ? <PropertiesPage /> : <LandingPage />}
          />

          {/* user protected routes */}
          {user && (
            <>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route
                path="properties/:propertyId"
                element={<PropertyLandingPage />}
              >
                <Route index element={<Navigate to="overview" />} />

                <Route
                  path="purchase-worksheet"
                  element={<PurchaseWorksheet />}
                />

                <Route path="overview" element={<PropertyOverview />} />

                <Route path="flip-worksheet" element={<FlipWorksheet />} />
                <Route path="flip-analysis" element={<FlipSummary />} />
                {/* <Route
                  path="property-assumptions"
                  element={<PropertyAssumptions />}
                /> */}
                <Route path="photos" element={<PropertyPhotoPage />} />
                <Route path="rehab-worksheet" element={<RehabWorksheet />} />
                <Route path="refinancing" element={<RefinancingWorksheet />} />

                <Route path="projections" element={<ComparisonProjections />} />
                <Route path="cash-flow" element={<ComparisonCashFlow />} />
                <Route path="map" element={<PropertyMapPage />} />
                <Route path="rent-roll" element={<RentRollPage />} />
                <Route path="summary" element={<SummaryPage />}>
                  <Route index element={<Navigate to="purchase-analysis" />} />

                  {/* purchase summary */}
                  <Route
                    path="purchase-analysis"
                    element={<PurchaseSummaryV2 />}
                  />

                  <Route
                    path="purchase-summary"
                    element={<PurchaseSummaryV2 />}
                  />

                  <Route
                    path="purchase-analysis-old"
                    element={<PurchaseSummary />}
                  />
                  {/* buy and hold routes  */}
                  <Route path="buy-and-hold" element={<RehabSummaryV2 />} />
                  <Route
                    path="rehab-buy-and-hold"
                    element={<RehabSummaryV2 />}
                  />
                  <Route
                    path="buy-and-hold-old"
                    element={<BuyAndHoldSummary />}
                  />
                  {/* brrr routes */}
                  <Route path="brrrr-analysis" element={<BRRRRSummaryV2 />} />
                  <Route path="brrrr-analysis-old" element={<BrrrrSummary />} />
                  <Route path="brrrr-summary" element={<BRRRRSummaryV2 />} />
                </Route>
              </Route>
              <Route path="users/" element={<Navigate to={user.uid} />} />
              <Route path="users/:userId" element={<UserProfilePage />} />

              <Route path="settings/" element={<Navigate to={user.uid} />} />
              <Route path="settings/:userId" element={<SettingsPage />} />
            </>
          )}

          {/* catch all route */}
          <Route
            path="*"
            element={user ? <Navigate to="not-found" /> : <Navigate to="/" />}
          />
        </Route>
      </Routes>
      {/* </Layout> */}
    </Suspense>
  );
}

export default AppRoutes;
